import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { 
  MerchantProductState,
  MerchantProductFilters,
  MerchantProduct,
  MerchantProductPagination
} from '../types'
import MerchantProductService from '../services/merchantProductService'
import type { ProductData } from '../components/ProductCreationWizard/ProductCreationWizard'
import { transformWizardDataToApiPayload } from '../utils/dataTransformers'

const initialState: MerchantProductState = {
  products: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    brandName: '',
    category: '',
    hasVariants: undefined,
    hasBundles: undefined,
    productType: 'all',
    isActive: true,
    priceRange: { min: null, max: null },
    salesRange: { min: null, max: null },
    sortBy: 'name',
    sortOrder: 'desc',
  },
  selectedProducts: [],
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  },
  productTypeCounts: {
    all: 0,
    variant: 0,
    single: 0,
    bundle: 0,
  },
  countsFetching: false,
  // Edit mode state
  editProduct: null,
  editLoading: false,
  editError: null,
}

// Async thunk for fetching merchant products
export const fetchMerchantProducts = createAsyncThunk(
  'merchantProduct/fetchMerchantProducts',
  async (params: { filters: MerchantProductFilters; page?: number; pageSize?: number }) => {
    const service = MerchantProductService.getInstance()
    return service.getMerchantProducts(
      params.filters,
      params.page || 1,
      params.pageSize || 20
    )
  }
)

// Async thunk for updating product stock
export const updateProductStock = createAsyncThunk(
  'merchantProduct/updateProductStock',
  async (params: { productId: string; quantity: number }) => {
    const service = MerchantProductService.getInstance()
    return service.updateProductStock(params.productId, params.quantity)
  }
)

// Async thunk for updating product price
export const updateProductPrice = createAsyncThunk(
  'merchantProduct/updateProductPrice',
  async (params: { productId: string; price: number }) => {
    const service = MerchantProductService.getInstance()
    return service.updateProductPrice(params.productId, params.price)
  }
)

// Async thunk for creating a new merchant product
export const createMerchantProduct = createAsyncThunk(
  'merchantProduct/createMerchantProduct',
  async (productData: ProductData) => {
    const service = MerchantProductService.getInstance()
    const apiPayload = transformWizardDataToApiPayload(productData)
    return service.createMerchantProduct(apiPayload)
  }
)

// Async thunk for updating an existing merchant product
export const updateMerchantProduct = createAsyncThunk(
  'merchantProduct/updateMerchantProduct',
  async (params: { productId: string; productData: ProductData }) => {
    const service = MerchantProductService.getInstance()
    const apiPayload = transformWizardDataToApiPayload(params.productData, true) // Edit mode = true
    return service.updateMerchantProduct(params.productId, apiPayload)
  }
)

// Async thunk for fetching product type counts
export const fetchProductTypeCounts = createAsyncThunk(
  'merchantProduct/fetchProductTypeCounts',
  async (baseFilters: Partial<MerchantProductFilters> = {}) => {
    const service = MerchantProductService.getInstance()
    return service.getProductTypeCounts(baseFilters)
  }
)

// Async thunk for fetching a single product for editing
export const fetchProductForEdit = createAsyncThunk(
  'merchantProduct/fetchProductForEdit',
  async (productId: string) => {
    const service = MerchantProductService.getInstance()
    const product = await service.getProductById(productId)
    if (!product) {
      throw new Error(`Product with ID ${productId} not found`)
    }
    return product
  }
)

const merchantProductSlice = createSlice({
  name: 'merchantProduct',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<MerchantProductFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload
    },
    setBrandFilter: (state, action: PayloadAction<string>) => {
      state.filters.brandName = action.payload
    },
    setCategoryFilter: (state, action: PayloadAction<string>) => {
      state.filters.category = action.payload
    },
    setActiveFilter: (state, action: PayloadAction<boolean>) => {
      state.filters.isActive = action.payload
    },
    setVariantFilter: (state, action: PayloadAction<boolean | undefined>) => {
      state.filters.hasVariants = action.payload
    },
    setBundleFilter: (state, action: PayloadAction<boolean | undefined>) => {
      state.filters.hasBundles = action.payload
    },
    setProductTypeFilter: (state, action: PayloadAction<'all' | 'variant' | 'single' | 'bundle'>) => {
      state.filters.productType = action.payload
      // Note: API v2 uses product_type parameter exclusively
      // hasVariants and hasBundles are deprecated and cannot be used with product_type
    },
    setPriceRange: (state, action: PayloadAction<{ min: number | null; max: number | null }>) => {
      state.filters.priceRange = action.payload
    },
    setSalesRange: (state, action: PayloadAction<{ min: number | null; max: number | null }>) => {
      state.filters.salesRange = action.payload
    },
    setSortField: (state, action: PayloadAction<{ field: MerchantProductFilters['sortBy']; direction: MerchantProductFilters['sortOrder'] }>) => {
      state.filters.sortBy = action.payload.field
      state.filters.sortOrder = action.payload.direction
    },
    setSelectedProducts: (state, action: PayloadAction<string[]>) => {
      state.selectedProducts = action.payload
    },
    toggleProductSelection: (state, action: PayloadAction<string>) => {
      const productId = action.payload
      if (state.selectedProducts.includes(productId)) {
        state.selectedProducts = state.selectedProducts.filter(id => id !== productId)
      } else {
        state.selectedProducts.push(productId)
      }
    },
    clearSelectedProducts: (state) => {
      state.selectedProducts = []
    },
    setPagination: (state, action: PayloadAction<Partial<MerchantProductPagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload
      state.pagination.currentPage = 1 // Reset to first page when changing page size
    },
    clearError: (state) => {
      state.error = null
    },
    resetState: () => initialState,
    forceRefresh: (state) => {
      // Clear current data to force a fresh fetch
      state.products = []
      state.error = null
      state.loading = false
      console.log('🔄 Force refresh triggered - cleared product data')
    },
    clearEditProduct: (state) => {
      state.editProduct = null
      state.editError = null
      state.editLoading = false
    },
    clearEditError: (state) => {
      state.editError = null
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchMerchantProducts
      .addCase(fetchMerchantProducts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMerchantProducts.fulfilled, (state, action) => {
        state.loading = false
        console.log('🔍 API Response:', action.payload)
        console.log('🔍 Product Data:', action.payload?.data?.product_merchants)

        if (action.payload?.success && action.payload.data?.product_merchants) {
          state.products = action.payload.data.product_merchants
          console.log('✅ Products loaded:', state.products.length)
          state.pagination = {
            currentPage: action.payload.data.pagination.page,
            pageSize: action.payload.data.pagination.page_size,
            totalItems: action.payload.data.pagination.total_count,
            totalPages: action.payload.data.pagination.total_pages,
            hasNext: action.payload.data.pagination.has_next,
            hasPrevious: action.payload.data.pagination.has_previous,
          }
        } else {
          console.warn('⚠️ API returned no products or failed')
          state.error = 'Failed to fetch merchant products or no data available'
          
          // Clear products and reset pagination when API returns no data
          state.products = []
          state.pagination = {
            ...state.pagination,
            totalItems: 0,
            totalPages: 1,
            hasNext: false,
            hasPrevious: false,
          }
        }
      })
      .addCase(fetchMerchantProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch merchant products'
        console.error('❌ API call failed:', action.error)
        
        // Clear products on error to trigger fallback to mock data
        state.products = []
        
        // Reset pagination to show "No products found"
        state.pagination = {
          ...state.pagination,
          totalItems: 0,
          totalPages: 1,
          hasNext: false,
          hasPrevious: false,
        }
      })
      // updateProductStock
      .addCase(updateProductStock.pending, (state) => {
        // Keep existing state, just track that an update is happening
      })
      .addCase(updateProductStock.fulfilled, (state, action) => {
        // Update the specific product in state if needed
        const productId = action.meta.arg.productId
        const productIndex = state.products.findIndex(p => p.id === productId)
        if (productIndex !== -1 && action.payload?.success) {
          // Could update local state here, or refetch to get latest data
        }
      })
      .addCase(updateProductStock.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update product stock'
      })
      // updateProductPrice
      .addCase(updateProductPrice.pending, (state) => {
        // Keep existing state, just track that an update is happening
      })
      .addCase(updateProductPrice.fulfilled, (state, action) => {
        // Update the specific product in state if needed
        const productId = action.meta.arg.productId
        const productIndex = state.products.findIndex(p => p.id === productId)
        if (productIndex !== -1 && action.payload?.success) {
          // Could update local state here, or refetch to get latest data
        }
      })
      .addCase(updateProductPrice.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update product price'
      })
      // fetchProductTypeCounts
      .addCase(fetchProductTypeCounts.pending, (state) => {
        state.countsFetching = true
      })
      .addCase(fetchProductTypeCounts.fulfilled, (state, action) => {
        state.countsFetching = false
        state.productTypeCounts = action.payload
      })
      .addCase(fetchProductTypeCounts.rejected, (state, action) => {
        state.countsFetching = false
        console.error('Failed to fetch product type counts:', action.error)
        // Keep existing counts on error
      })
      // createMerchantProduct
      .addCase(createMerchantProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createMerchantProduct.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.success) {
          console.log('✅ Product created successfully:', action.payload.data)
          
          // Optimistic update: Add new product to current state if it matches current filters
          const newProduct = action.payload.data
          if (newProduct) {
            // Check if new product matches current filter criteria
            const shouldIncludeProduct = (
              // Active filter matches
              (state.filters.isActive === undefined || state.filters.isActive === newProduct.isActive) &&
              // Product type filter matches
              (state.filters.productType === 'all' || 
               (state.filters.productType === 'single' && !newProduct.isProductHasVariants && !newProduct.isBundle) ||
               (state.filters.productType === 'variant' && newProduct.isProductHasVariants) ||
               (state.filters.productType === 'bundle' && newProduct.isBundle))
            )
            
            if (shouldIncludeProduct) {
              // Add to products list and update pagination
              state.products.unshift(newProduct) // Add at beginning for immediate visibility
              state.pagination.totalItems += 1
              
              // Update product type counts optimistically
              state.productTypeCounts.all += 1
              if (!newProduct.isProductHasVariants && !newProduct.isBundle) {
                state.productTypeCounts.single += 1
              } else if (newProduct.isProductHasVariants) {
                state.productTypeCounts.variant += 1
              } else if (newProduct.isBundle) {
                state.productTypeCounts.bundle += 1
              }
              
              console.log('🔄 Optimistically added new product to state:', newProduct.name)
            }
          }
        } else {
          state.error = 'Failed to create product'
        }
      })
      .addCase(createMerchantProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create product'
        console.error('❌ Product creation failed:', action.error)
      })
      // updateMerchantProduct
      .addCase(updateMerchantProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateMerchantProduct.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.success) {
          console.log('✅ Product updated successfully:', action.payload.data)
          // Optionally update the product in the state
          // We'll likely want to refetch products instead
        } else {
          state.error = 'Failed to update product'
        }
      })
      .addCase(updateMerchantProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update product'
        console.error('❌ Product update failed:', action.error)
      })
      // fetchProductForEdit
      .addCase(fetchProductForEdit.pending, (state) => {
        state.editLoading = true
        state.editError = null
      })
      .addCase(fetchProductForEdit.fulfilled, (state, action) => {
        state.editLoading = false
        state.editProduct = action.payload
        console.log('✅ Product loaded for editing:', action.payload.id, action.payload.name)
      })
      .addCase(fetchProductForEdit.rejected, (state, action) => {
        state.editLoading = false
        state.editError = action.error.message || 'Failed to load product for editing'
        state.editProduct = null
        console.error('❌ Product load for edit failed:', action.error)
      })
  },
})

export const {
  setFilters,
  setSearchTerm,
  setBrandFilter,
  setCategoryFilter,
  setActiveFilter,
  setVariantFilter,
  setBundleFilter,
  setProductTypeFilter,
  setPriceRange,
  setSalesRange,
  setSortField,
  setSelectedProducts,
  toggleProductSelection,
  clearSelectedProducts,
  setPagination,
  setPage,
  setPageSize,
  clearError,
  resetState,
  forceRefresh,
  clearEditProduct,
  clearEditError,
} = merchantProductSlice.actions

// Note: fetchMerchantProducts, createMerchantProduct, updateMerchantProduct, updateProductStock, 
// updateProductPrice, fetchProductTypeCounts, and fetchProductForEdit are already exported above as async thunks using createAsyncThunk

export default merchantProductSlice.reducer